import React from 'react';
import { FormattedMessage } from 'react-intl';
import { alpha, Box, IconButton, Paper } from '@mui/material';
import { Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';

import { Icon, FormattedDateTimeRange, Text, useSettings } from 'app/main/components';
import { type WbsRootNode, type WbsNode } from './useWbsState';
import wbsSettings from './WbsGraphSettings';

interface RootNodeProps {
	data: WbsRootNode;
}

interface BranchNodeProps {
	data: WbsNode;
}

interface LeafNodeProps {
	data: WbsNode;
}

const { NODE_WIDTH, NODE_HEIGHT } = wbsSettings;

const RootNode = React.memo(({ data }: RootNodeProps) => {
	const { table, title, dateFrom, dateTo, selected, isEditable, onNewNodeAdd } = data;
	const { settings, t } = useSettings(table);

	return (
		<Paper
			className="flex items-center rounded-6 p-12"
			variant="outlined"
			sx={theme => ({
				height: NODE_HEIGHT,
				// border: `2px solid ${theme.palette.primary.main}`,
				// backgroundColor: alpha(theme.palette.primary.light, 0.2)
				borderWidth: 2,
				borderStyle: 'solid',
				borderColor: selected ? theme.palette.secondary.light : theme.palette.primary.main,
				backgroundColor: alpha(selected ? theme.palette.secondary.light : theme.palette.primary.light, 0.2)
			})}
		>
			<Icon className="mr-8" icon={settings.modu_icon} size={36} />
			<Box className="mr-40 w-full">
				<Text className="font-600" variant="subtitle2">
					{title}
				</Text>
				<Text className="font-400" variant="subtitle2" color="text.secondary">
					{dateFrom || dateTo ? (
						<FormattedDateTimeRange from={dateFrom} to={dateTo} />
					) : (
						t('VIEW_ITEM_NO_DATES')
					)}
				</Text>
			</Box>
			{isEditable && (
				<IconButton
					className="ml-8"
					onClick={event => {
						onNewNodeAdd({ parentId: 'root', parentTitle: title });
						event.stopPropagation();
					}}
				>
					<Icon icon="plus-circle" />
				</IconButton>
			)}
			<Handle type="source" position={Position.Bottom} id="child" style={{ bottom: 0, left: 16, opacity: 0 }} />
		</Paper>
	);
});

const BranchNode = React.memo(({ data }: BranchNodeProps) => {
	const { t } = useSettings();
	const { node, parentId, selected, expanded, isEditable, onBranchExpand, onNewNodeAdd } = data;

	const switchExpanded = () => onBranchExpand(node.wbsn_uid, !expanded);

	const addNewNode = () => onNewNodeAdd({ parentId: node.wbsn_uid, parentTitle: node.wbsn_title });

	return (
		<Paper
			className="flex w-360 items-center rounded-6 p-12 pr-72"
			variant="outlined"
			sx={theme => ({
				height: NODE_HEIGHT,
				borderWidth: 2,
				borderStyle: 'solid',
				borderColor: selected ? theme.palette.secondary.light : theme.palette.primary.main,
				backgroundColor: alpha(selected ? theme.palette.secondary.light : theme.palette.primary.light, 0.2)
			})}
		>
			<Icon className="mr-8" icon="folder-outline" size={36} />
			<Box>
				<Text className="font-600" variant="subtitle2">
					{node.wbsn_index} &#8211; {node.wbsn_title}
				</Text>
				<Text className="font-400" variant="subtitle2" color="text.secondary">
					<FormattedMessage
						id="projectsWbsNode.duration"
						defaultMessage={t('WBS_DURATION')}
						values={{ value: node.wbsn_duration || '-' }}
					/>
				</Text>
			</Box>
			{isEditable && (
				<Box className="absolute right-8">
					<IconButton size="small" onClick={addNewNode}>
						<Icon icon="plus-circle" />
					</IconButton>
					<IconButton size="small" onClick={switchExpanded}>
						<Icon icon={expanded ? 'chevron-up' : 'chevron-down'} />
					</IconButton>
				</Box>
			)}
			<Handle type="target" position={Position.Left} id={parentId} style={{ left: 0, opacity: 0 }} />
			<Handle type="source" position={Position.Bottom} id="child" style={{ bottom: 0, left: 16, opacity: 0 }} />
		</Paper>
	);
});

const LeafNode = React.memo(({ data }: LeafNodeProps) => {
	const { t } = useSettings();
	const { node, parentId, selected } = data;

	return (
		<Paper
			className="flex w-360 items-center rounded-6 p-12"
			variant="outlined"
			sx={theme => ({
				height: NODE_HEIGHT,
				borderWidth: selected ? 2 : 1,
				borderStyle: 'solid',
				borderColor: selected ? theme.palette.secondary.light : theme.palette.divider,
				backgroundColor: selected ? alpha(theme.palette.secondary.light, 0.2) : undefined
			})}
		>
			<Box>
				<Text className="font-500" variant="subtitle2">
					{node.wbsn_index} &#8211; {node.wbsn_title}
				</Text>
				<Text className="font-400" variant="subtitle2" color="text.secondary">
					<FormattedMessage
						id="projectsWbsNode.duration"
						defaultMessage={t('WBS_DURATION')}
						values={{ value: node.wbsn_duration || '-' }}
					/>
				</Text>
			</Box>
			<Handle type="target" position={Position.Left} id={parentId} style={{ left: 0, opacity: 0 }} />
		</Paper>
	);
});

export default {
	rootNode: RootNode,
	branchNode: BranchNode,
	leafNode: LeafNode
};
